import { useEffect } from "react";
import { RecupererRapport } from "../components/RecupererRapport";
import { delaiReponseSupport } from "../content/support";
import { useApp } from "../state/AppState";
import { track } from "../lib/track";
import type { EventName } from "../lib/track";

/**
 * Un problème, une demande — la page qui manquait entièrement.
 *
 * Avant elle, un acheteur dont le rapport n'arrivait pas avait une adresse de
 * courriel dans le pied de page, et rien d'autre : ni ce qu'il fallait écrire,
 * ni ce qui se passerait ensuite, ni combien de temps cela prendrait. Une
 * boîte aux lettres n'est pas un support.
 *
 * Trois principes tiennent cette page.
 *
 * 1. **Ce qui peut se régler seul se règle seul.** Le motif le plus fréquent
 *    et le plus urgent — « j'ai payé, je n'ai pas mon rapport » — se répare ici
 *    même, en dix secondes, sans qu'un humain intervienne. C'est le seul délai
 *    de réponse qu'on puisse honnêtement promettre aujourd'hui : celui d'un
 *    formulaire, qui est nul.
 *
 * 2. **Aucun délai n'est annoncé tant qu'aucun n'est engagé.** `null` veut dire
 *    « cet engagement n'existe pas encore » — même discipline que les dix
 *    mentions légales. Écrire « réponse sous 48 heures » parce que ça rassure
 *    serait un faux signal au sens de la règle n° 6, et le pire de tous : il ne
 *    se découvre qu'au moment où quelqu'un attend.
 *
 * 3. **La demande laisse une trace.** Chaque motif ouvert est compté, par
 *    motif. Le motif dominant est l'information la plus utile : si les gens
 *    écrivent surtout pour un rapport manquant, c'est la livraison qu'il faut
 *    réparer, pas le support.
 */

interface Motif {
  readonly cle: string;
  readonly titre: string;
  /** Ce qui part dans l'objet du courriel, pour que la demande arrive triée. */
  readonly objet: string;
  readonly evenement: EventName;
}

/*
  Les quatre motifs, et leurs quatre évènements écrits d'avance. Un nom de
  compteur construit à partir d'une saisie permettrait d'écrire n'importe où
  dans le document de mesures — la règle vaut ici comme ailleurs.
*/
const MOTIFS: readonly Motif[] = [
  {
    cle: "rapport",
    titre: "Je n'ai pas reçu mon rapport",
    objet: "Rapport non reçu",
    evenement: "support_rapport",
  },
  {
    cle: "remboursement",
    titre: "Je demande le remboursement",
    objet: "Demande de remboursement",
    evenement: "support_remboursement",
  },
  {
    cle: "donnees",
    titre: "Mes données",
    objet: "Demande sur mes données",
    evenement: "support_donnees",
  },
  {
    cle: "autre",
    titre: "Autre chose",
    objet: "Question",
    evenement: "support_autre",
  },
];

export function Aide() {
  const { contactCourriel } = useApp();
  const delai = delaiReponseSupport();

  useEffect(() => {
    document.title = "Un problème, une demande | STRATERA";
  }, []);

  /** Une adresse pré-remplie : la demande arrive triée, et déjà datée. */
  const ecrire = (motif: Motif): string => {
    const objet = encodeURIComponent(`STRATERA — ${motif.objet}`);
    return `mailto:${contactCourriel}?subject=${objet}`;
  };

  return (
    <section id="contenu">
      <div className="shell narrow app">
        <div className="kicker">Aide</div>
        <h1 style={{ marginTop: "6px" }}>Un problème, une demande</h1>
        <p className="sub">
          Quatre situations, et ce qui se passe dans chacune. La première se règle sur cette page,
          sans nous écrire.
        </p>

        <div className="block">
          <h2>Vous avez payé et le rapport n'est pas arrivé</h2>
          <p>
            C'est la demande la plus fréquente, et elle n'a pas besoin de nous. Votre rapport reste
            disponible : entrez la référence reçue au moment du paiement, et la page retrouve
            aussitôt l'état de votre commande — le rapport s'ouvre dès qu'il est prêt.
          </p>
          <RecupererRapport ouvertParDefaut />
          <p className="fine">
            La référence ne correspond à rien ? Écrivez-nous avec la date, l'heure approximative du
            paiement et le numéro utilisé — ce sont les trois éléments qui permettent de retrouver
            une transaction.
          </p>
        </div>

        <div className="block">
          <h2>Vous voulez être remboursé</h2>
          <p>
            Le remboursement est <b>sans justification</b>, dans les quatorze jours suivant
            l'achat. Vous n'avez pas à expliquer pourquoi vous le demandez, et
            personne ne vous le demandera. Écrivez avec la référence de votre paiement ; le
            remboursement part sur le même numéro que celui du débit.
          </p>
        </div>

        <div className="block">
          <h2>Vos données</h2>
          <p>
            Accès, rectification, effacement : la demande se fait à la même adresse. Ce que nous
            détenons est décrit dans les <a href="/#legal">mentions de confidentialité</a>, et ce
            que votre appareil garde s'efface depuis cette même page, sans nous écrire.
          </p>
        </div>

        <div className="block">
          <h2>Écrire</h2>
          <div className="portes portes-quatre" role="group" aria-label="Motif de votre demande">
            {MOTIFS.map((motif) => (
              <a
                key={motif.cle}
                href={ecrire(motif)}
                onClick={() => track(motif.evenement, {})}
              >
                {motif.titre}
              </a>
            ))}
          </div>
          <p className="fine" style={{ marginTop: "12px" }}>
            Chaque lien ouvre votre messagerie avec l'objet déjà rempli. Si rien ne
            s'ouvre, écrivez-nous depuis n'importe quelle adresse à {contactCourriel}, en indiquant
            votre référence de paiement. C'est ce tri qui fait qu'une demande urgente ne se perd
            pas derrière vingt questions générales.
          </p>
        </div>

        <div className="block">
          <h2>Ce qui se passe ensuite</h2>
          <ol className="listy">
            <li>
              <span>
                <b>Votre message est lu par une personne</b>, pas par un automate. STRATERA est
                édité par une petite équipe : il n'y a ni file d'attente, ni numéro de ticket, ni
                réponse type.
              </span>
            </li>
            <li>
              <span>
                <b>Un remboursement est exécuté sans discussion.</b> C'est la seule demande qui ne
                donne lieu à aucune question de notre part.
              </span>
            </li>
            <li>
              <span>
                {/*
                  Le passage qui décide de l'honnêteté de toute la page. Tant
                  qu'aucun délai n'est engagé, il est nommé absent — jamais
                  remplacé par un chiffre plausible.
                */}
                {delai === null ? (
                  <>
                    <b>Le délai de réponse n'est pas encore engagé.</b> Nous préférons ne rien
                    promettre plutôt qu'annoncer un délai que nous ne tiendrions pas tous les jours.
                    Il sera publié ici le jour où il le sera vraiment. En attendant, la seule
                    promesse qui vaut est celle du haut de page : un rapport payé se récupère
                    immédiatement, sans nous attendre.
                  </>
                ) : (
                  <>
                    <b>Nous répondons sous {delai}.</b> C'est un engagement, pas une moyenne.
                  </>
                )}
              </span>
            </li>
          </ol>
        </div>
      </div>
    </section>
  );
}
